<script setup lang="ts">
import { computed, ref } from "vue";
import { PopoverClose } from "reka-ui";

import { useMessageStore, useRetentionStore } from "@/stores";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import { isApiError } from "@/services/Api";
import { toISODuration } from "@/services/UtilitiesService";

import RetentionActionPopover from "./RetentionActionPopover.vue";
import RetentionActionConfirmCase from "./RetentionActionConfirmCase.vue";

const caseRouteParams = useCaseRouteParams();

const reason = ref("");
const caseRefInput = ref("");

const months = ref<number>();
const days = ref<number>();
const hours = ref<number>();
const duration = computed(() => toISODuration({ month: months.value, day: days.value, hour: hours.value }));

const state = ref<{ status: "idle" } | { status: "loading" } | { status: "error"; message: string }>({
  status: "idle",
});

const isSubmitDisabled = computed(
  () =>
    state.value.status === "loading" || caseRefInput.value !== caseRouteParams.value.casename || duration.value == null,
);

async function handleSubmit() {
  if (isSubmitDisabled.value) return;

  state.value = { status: "loading" };

  try {
    await useRetentionStore().recoverCase({ reason: reason.value, duration: duration.value! });

    state.value = { status: "idle" };

    useMessageStore().updateMessage({
      type: "success",
      autoHide: true,
      showReload: false,
      title: "Case Retention",
      message: "Case recovered successfully.",
    });
  } catch (error) {
    const message =
      isApiError<{ reason?: string }>(error) && error.status === 406 && error.data?.reason
        ? error.data.reason
        : "Failed to recover case. Please try again.";

    state.value = { status: "error", message };

    useMessageStore().updateMessage({
      type: "error",
      autoHide: true,
      showReload: false,
      title: "Case Retention",
      message,
    });
  }
}
</script>
<template>
  <RetentionActionPopover>
    <template #trigger>
      <button type="button" class="tw-btn-md-gray tw-b-r">Recover Case</button>
    </template>
    <form class="tw-flex tw-flex-col tw-gap-2" @submit.prevent="handleSubmit">
      <p class="tw-max-w-md tw-text-balance tw-text-sm">
        You are about to recover this case from the deletion process. It will be restored to an active retained state.
      </p>
      <hr />
      <fieldset class="tw-flex tw-flex-col tw-gap-1">
        <legend class="tw-text-xs">New retention duration</legend>
        <div class="tw-flex tw-gap-2">
          <div class="tw-flex tw-flex-1 tw-flex-col tw-gap-0.5">
            <label for="edit-retention-duration-months" class="tw-text-xs">Months</label>
            <input
              id="edit-retention-duration-months"
              v-model.number="months"
              type="number"
              min="0"
              class="tw-w-full tw-rounded tw-border tw-p-2 tw-text-sm"
              placeholder="0"
              aria-label="Months"
            />
          </div>
          <div class="tw-flex tw-flex-1 tw-flex-col tw-gap-0.5">
            <label for="edit-retention-duration-days" class="tw-text-xs">Days</label>
            <input
              id="edit-retention-duration-days"
              v-model.number="days"
              type="number"
              min="0"
              class="tw-w-full tw-rounded tw-border tw-p-2 tw-text-sm"
              placeholder="0"
              aria-label="Days"
            />
          </div>
          <div class="tw-flex tw-flex-1 tw-flex-col tw-gap-0.5">
            <label for="edit-retention-duration-hours" class="tw-text-xs">Hours</label>
            <input
              id="edit-retention-duration-hours"
              v-model.number="hours"
              type="number"
              min="0"
              class="tw-w-full tw-rounded tw-border tw-p-2 tw-text-sm"
              placeholder="0"
              aria-label="Hours"
            />
          </div>
        </div>
      </fieldset>
      <hr />
      <RetentionActionConfirmCase v-model:case-ref="caseRefInput" v-model:reason="reason" />
      <p v-if="state.status === 'error'" role="alert" class="tw-text-sm tw-text-red-600">
        {{ state.message }}
      </p>
      <div class="tw-flex tw-items-center tw-justify-end tw-gap-1">
        <PopoverClose class="tw-btn-md-white tw-b-r">Cancel</PopoverClose>
        <button
          type="submit"
          class="tw-btn-md-gray tw-b-r tw-flex tw-items-center tw-gap-1"
          :disabled="isSubmitDisabled"
        >
          <i v-if="state.status === 'loading'" aria-hidden="true" class="fas fa-spin fa-circle-notch" />
          Recover Case
        </button>
      </div>
    </form>
  </RetentionActionPopover>
</template>
